import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    viewChildren,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { applyEach, FormField, FieldTree, form, validate } from "@angular/forms/signals";
import { Layouts } from "./se.layouts";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25Util } from "../../../util/s25-util";
import { S25SimpleCollapseModule } from "../../s25-simple-collapse/s25.simple.collapse.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { s25Resource } from "../../../util/s25.resource";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { Bind } from "../../../decorators/bind.decorator";
import { SeLayoutsStore } from "./se.layouts.store";
import { SeLayoutConfigSectionComponent } from "./se.layout.config.section.component";
import { RequiredBy } from "../../../pojo/Util";
import { ModalService } from "../../modal/modal.service";
import { TelemetryService } from "../../../services/telemetry.service";
import { S25SimpleCollapseComponent } from "../../s25-simple-collapse/s25.simple.collapse.component";
import { S25Assert } from "../../../util/s25.assert";

@Component({
    standalone: true,
    selector: "s25-ng-se-layout-config",
    template: `
        @if (isLoading()) {
            <s25-ng-loading-inline-static />
        } @else if (errorLoading()) {
            Something went wrong
        } @else {
            <section class="surface">
                <form class="config-form" action="javascript:void(0);">
                    @if (!nameless()) {
                        <div class="form-row">
                            <label for="layout-config--name">Name</label>
                            @if (readonly()) {
                                <p>{{ configForm.name().value() }}</p>
                            } @else {
                                <input id="layout-config--name" type="text" [formField]="configForm.name" />
                                @for (error of configForm.name().errors(); track $index) {
                                    <p class="field-error">{{ error.message }}</p>
                                }
                            }
                        </div>
                        <div class="form-row">
                            <label for="layout-config--description">Description</label>
                            @if (readonly()) {
                                <p>{{ configForm.description().value() }}</p>
                            } @else {
                                <textarea
                                    id="layout-config--description"
                                    [formField]="configForm.description"
                                ></textarea>
                                @for (error of configForm.description().errors(); track $index) {
                                    <p class="field-error">{{ error.message }}</p>
                                }
                            }
                        </div>
                    }

                    <!-- Configure sections -->
                    <div>
                        <div class="divider-label">
                            <h3 class="label">Sections</h3>
                            <s25-ng-button
                                [type]="'outline'"
                                (clicked)="expandOrCollapseAll()"
                                [disabled]="!configForm.sections().value().length"
                            >
                                {{ allCollapsed() ? "Expand" : "Collapse" }} All
                            </s25-ng-button>
                        </div>
                        <div class="sections">
                            @for (section of configForm.sections; track section; let s = $index) {
                                <ng-template #sectionHeader>
                                    <div class="collapse-header">
                                        <span>
                                            {{ section.heading().value() || "Section" }}
                                        </span>
                                        @if (!readonly()) {
                                            <div class="buttons icon-buttons">
                                                <s25-ng-button
                                                    [type]="'outline'"
                                                    [disabled]="$index === 0"
                                                    (clicked)="moveItem(configForm.sections, section, -1)"
                                                    [ariaLabel]="'Move up'"
                                                >
                                                    <s25-ng-icon [type]="'arrowUp'" [ariaLabel]="false" />
                                                </s25-ng-button>
                                                <s25-ng-button
                                                    [type]="'outline'"
                                                    [disabled]="$index === configForm.sections.length - 1"
                                                    (clicked)="moveItem(configForm.sections, section, 1)"
                                                    [ariaLabel]="'Move down'"
                                                >
                                                    <s25-ng-icon [type]="'arrowDown'" [ariaLabel]="false" />
                                                </s25-ng-button>
                                                <s25-ng-button
                                                    [type]="'danger--outline'"
                                                    (clicked)="removeItem(configForm.sections, section)"
                                                    [ariaLabel]="'Delete'"
                                                >
                                                    <s25-ng-icon [type]="'trash2'" [ariaLabel]="false" />
                                                </s25-ng-button>
                                            </div>
                                        }
                                    </div>
                                </ng-template>
                                <s25-simple-collapse
                                    class="section"
                                    [header]="sectionHeader"
                                    [buttonsSide]="'left'"
                                    [class.invalid]="!section().valid()"
                                    [expanded]="$first"
                                >
                                    <s25-ng-se-layout-config-section
                                        class="collapse-content"
                                        [pageMeta]="Layouts.pages[page()]"
                                        [section]="section"
                                        [readonly]="readonly()"
                                    />
                                </s25-simple-collapse>
                            }
                            @if (!readonly()) {
                                @for (error of configForm.sections().errors(); track $index) {
                                    <p class="field-error">{{ error.message }}</p>
                                }
                            }
                        </div>
                        @if (!readonly()) {
                            <div class="buttons footer-buttons">
                                <s25-ng-button [type]="'outline'" (clicked)="addSection()">+ Add Section</s25-ng-button>
                            </div>
                        }
                    </div>
                </form>
            </section>
            <div class="buttons">
                @if (readonly()) {
                    @if (configForm().dirty()) {
                        <s25-ng-button [type]="'primary'" [onClick]="save"> Save</s25-ng-button>
                    }
                    <s25-ng-button [type]="'outline'" (clicked)="exit()">Exit</s25-ng-button>
                } @else {
                    <s25-ng-button [type]="'primary'" [onClick]="save" [disabled]="!canSave()"> Save</s25-ng-button>
                    <s25-ng-button [type]="'danger--outline'" (clicked)="cancel()">Cancel</s25-ng-button>
                }
            </div>
        }
    `,
    styles: `
        :host {
            display: grid;
            gap: 0.5rem;
        }

        :host ::ng-deep h3,
        :host ::ng-deep h4,
        :host ::ng-deep h5,
        :host ::ng-deep h6 {
            margin: 0;
        }

        :host ::ng-deep label {
            color: var(--color--text-muted);
        }

        :host ::ng-deep input[type="text"],
        :host ::ng-deep input[type="number"],
        :host ::ng-deep textarea {
            border: 1px solid var(--color--border) !important;
            border-radius: 6px;
            font-size: 0.875rem;
            background: var(--color--surface-bg);
            padding: 0.5rem 0.75rem;
            color: var(--color--text);
        }

        :host ::ng-deep select {
            border: 1px solid var(--color--border) !important;
            width: fit-content;
        }

        :host ::ng-deep textarea {
            min-height: 6rem;
            resize: vertical;
        }

        :host ::ng-deep .surface {
            background: var(--color--surface-bg);
            padding: 1rem;
            border-radius: 6px;
            border: 1px solid var(--color--border);
        }

        .config-form {
            display: grid;
            row-gap: 1rem;
        }

        .sections {
            display: grid;
            row-gap: 1rem;
        }

        :host ::ng-deep .form-row {
            display: grid;
            row-gap: 0.25rem;
        }

        :host ::ng-deep .buttons {
            display: flex;
            justify-content: center;
            gap: 0.5rem;
            font-size: 1rem;
        }

        :host ::ng-deep .icon-buttons {
            justify-content: space-between;

            button {
                padding: 0.25em;
                aspect-ratio: 1;
            }
        }

        :host ::ng-deep s25-simple-collapse {
            --simple-collapse--header-bg-color: var(--color--page-bg);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            border-radius: 8px;
        }

        :host ::ng-deep s25-simple-collapse.invalid,
        :host ::ng-deep s25-simple-collapse:has(.field-error) {
            --simple-collapse--border-color: var(--error-message-color);
            --simple-collapse--header-bg-color: var(--color--surface-bg-error);
        }

        :host ::ng-deep s25-simple-collapse .header-container {
            overflow: hidden;
        }

        :host ::ng-deep s25-simple-collapse .collapse-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            color: var(--color--text-muted);
            padding-block: 0.7em;
            font-weight: 600;
        }

        :host ::ng-deep s25-simple-collapse .collapse-header span {
            overflow: hidden;
            text-overflow: ellipsis;
        }

        :host ::ng-deep s25-simple-collapse .collapse-header .buttons {
            padding-inline: 0.5rem;
        }

        :host ::ng-deep s25-simple-collapse .collapse-content {
            padding: 1rem;
            display: block;

            --simple-collapse--border-color: initial;
            --simple-collapse--header-bg-color: initial;
        }

        :host ::ng-deep s25-simple-collapse {
            margin: 0;
            border-radius: 8px;

            > .c-sectionHead {
                border-radius: 8px;
            }

            &.expanded > .c-sectionHead {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }
        }

        .collapse-header {
            font-size: 1.25em;
        }

        :host ::ng-deep .divider-label {
            border-bottom: 1px solid var(--color--border);
            padding-block: 0.5rem;
            margin-bottom: 0.5rem;
            display: flex;
            justify-content: space-between;
        }

        :host ::ng-deep .divider-label .label {
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--color--text-muted);
            font-size: 1em !important;
            align-content: end;
        }

        :host ::ng-deep .aw-button[disabled] {
            color: var(--color--text-muted) !important;
        }

        :host ::ng-deep .footer-buttons {
            justify-content: end;
            padding-block-start: 1rem;
        }

        :host ::ng-deep .field-error {
            color: var(--error-message-color);
            font-size: 0.9em;
            grid-column: 1/-1;
        }

        :host ::ng-deep .visibility .s25-multiselect,
        :host ::ng-deep .visibility .s25-multiselect-result,
        :host ::ng-deep .visibility .s25-multiselect-popup-position-header {
            display: block;
        }

        :host ::ng-deep .visibility .visibility-label {
            display: flex;
            gap: 0.5rem;
            align-items: center;
        }

        :host ::ng-deep s25-ng-css-rule select {
            border-radius: 8px;
        }

        :host ::ng-deep s25-ng-css-rule input {
            padding: 0 !important;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ReactiveFormsModule,
        FormField,
        S25ButtonComponent,
        S25SimpleCollapseModule,
        S25IconComponent,
        FormsModule,
        S25LoadingInlineStaticComponent,
        SeLayoutConfigSectionComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeLayoutConfigComponent {
    readonly page = input.required<Layouts.Page>();
    readonly editMode = input.required<"create" | "edit" | "copy">();
    readonly layoutId = input.required<number | undefined>();
    readonly nameless = input(false);
    readonly orgId = input<number | undefined>(undefined);
    readonly category = input.required<Layouts.Category>();
    readonly readonly = input(false);

    readonly saved = output<void>();
    readonly cancelled = output<void>();

    readonly collapses = viewChildren(S25SimpleCollapseComponent);
    readonly allCollapsed = computed(() => !!this.collapses()?.every((collapse) => !collapse.expanded()));

    protected readonly Layouts = Layouts;
    readonly store = inject(SeLayoutsStore);
    protected readonly ModalService = ModalService;

    readonly layout = s25Resource({
        params: () => ({ layoutId: this.layoutId() }),
        loader: async ({ params: { layoutId }, abortSignal }) => {
            const id = layoutId ?? this.store.getDefaultLayoutId(this.page());
            const model = id
                ? await this.store.getLayout(id, { abortSignal })
                : structuredClone(Layouts.defaultLayouts[this.page()]);
            if (!model) return;
            const editMode = this.editMode();
            switch (editMode) {
                case "edit":
                    return model;
                case "create":
                    return { ...model, id: undefined };
                case "copy":
                    return { ...model, id: undefined, name: `${model.name} Copy` };
                default:
                    return S25Assert.never(editMode, "Unhandled layout edit mode");
            }
        },
    });
    readonly isLoading = computed(() => this.layout.resource.isLoading());
    readonly errorLoading = computed(() => this.layout.resource.error());

    readonly config = linkedSignal(() => {
        const layout = this.layout.value();
        if (!layout) return undefined;

        return {
            ...layout,
            name: layout.name ?? null,
            description: layout.description ?? null,
            heading: layout.heading ?? null,
            sections: layout!.sections.map((section) => ({
                ...section,
                visibleToRoles: section.visibleToRoles ?? [],
                heading: section.heading ?? null,
                headingCss: section.headingCss ?? null,
                columns: section.columns ?? 1,
                widgets: section.widgets.map((widget) => {
                    const pageMeta = Layouts.pages[this.page()];
                    const widgetMeta = pageMeta.widgets[widget.type].META;
                    const defaultSettings = Layouts.getDefaultSettingsForWidget(widgetMeta);
                    return { ...widget, settings: { ...defaultSettings, ...widget.settings } };
                }),
            })),
        };
    });
    readonly configForm = form(
        <WritableSignal<RequiredBy<Layouts.Layout, "name" | "description">>>this.config,
        (schema) => {
            validate(schema.name, (field) => {
                if (this.nameless()) return;
                const value = field.value();
                if (!value) {
                    return { kind: "field-length", message: `Enter a name` };
                }
                if (value.length > Layouts.NAME_MAX_LENGTH) {
                    return {
                        kind: "field-length",
                        message: `Name must be ${Layouts.NAME_MAX_LENGTH} characters or less`,
                    };
                }
                if (this.store.isNameTaken(value, this.layoutId())) {
                    return { kind: "duplicate-name", message: "Name is already taken" };
                }
            });
            validate(schema.description, ({ value }) => {
                if (this.nameless()) return;
                if (value().length > Layouts.DESCRIPTION_MAX_LENGTH) {
                    return {
                        kind: "field-length",
                        message: `Description must be ${Layouts.DESCRIPTION_MAX_LENGTH} characters or less`,
                    };
                }
            });
            validate(schema.sections, (field) => {
                if (!field.value().length) {
                    return {
                        kind: "field-length",
                        message: `At least one section is required`,
                    };
                }
            });
            applyEach(schema.sections, (section) => {
                validate(section.heading, (field) => {
                    const value = field.value();
                    if (value && value.length > Layouts.SECTION_HEADING_MAX_LENGTH) {
                        return {
                            kind: "field-length",
                            message: `Heading must be ${Layouts.SECTION_HEADING_MAX_LENGTH} characters or less`,
                        };
                    }
                });

                validate(section.widgets, (field) => {
                    if (!field.value().length) {
                        return {
                            kind: "field-length",
                            message: `At least one widget is required`,
                        };
                    }
                });

                applyEach(section.widgets, (widget) => {
                    applyEach(widget.settings, (setting) => {
                        validate(setting.value, (field) => {
                            if (field.value().length > Layouts.SETTING_VALUE_MAX_LENGTH) {
                                return {
                                    kind: "field-length",
                                    message: `Value must be ${Layouts.SETTING_VALUE_MAX_LENGTH} characters or less`,
                                };
                            }
                        });
                    });
                });
            });
        },
    );

    readonly canSave = computed(() => {
        if (!this.configForm().valid()) return false;
        if (!this.configForm().dirty() && this.editMode() === "edit") return false;
        return true;
    });

    addSection() {
        this.configForm.sections().value.update((sections) => [
            ...sections,
            {
                heading: "",
                headingCss: "",
                order: sections.length,
                visibleToRoles: [],
                columns: 1,
                widgets: [],
            } satisfies Layouts.Section,
        ]);
        this.configForm.sections().markAsDirty();
    }

    moveItem<T>(items: FieldTree<T[], string>, item: FieldTree<T>, dir: -1 | 1) {
        const index = items().value().indexOf(item().value());
        const newIndex = index + dir;
        if (index === -1 || newIndex < 0 || newIndex >= items().value().length) return;
        items().value.update((items) => {
            const newItems = [...items];
            [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
            return newItems;
        });
        items().markAsDirty();
    }

    removeItem<T>(items: FieldTree<T[], string>, item: FieldTree<T>) {
        const index = items().value().indexOf(item().value());
        if (index === -1) return;
        items().value.update((items) => [...items.slice(0, index), ...items.slice(index + 1)]);
        items().markAsDirty();
    }

    @Bind
    async save() {
        if (!this.canSave()) return;
        const model = <Layouts.Layout>{ ...this.config()! };
        delete model.name;
        delete model.description;
        model.orgId = this.orgId();
        model.category = Layouts.categoryFromOrgId(this.orgId());
        if (!Layouts.isPageOverride(model)) {
            model.id = this.store.getDefaultLayoutId(this.page()) ?? model.id;
        }
        const isCreate = this.editMode() !== "edit";
        const [ok, err] = await S25Util.Maybe(this.store.updateLayout(model));
        if (!!err) {
            alert("Something went wrong when saving the layout");
            return;
        }
        TelemetryService.sendWithSub("StudentEngagement", "Layouts", isCreate ? "LayoutCreated" : "LayoutSaved");
        this.saved.emit();
    }

    cancel() {
        this.cancelled.emit();
    }

    async exit() {
        if (this.configForm().dirty()) {
            const ok = await this.ModalService.dialog({
                type: "Yes No",
                primary: "No",
                title: "Are you sure?",
                message: `Are you sure you want to exit? Unsaved changes will be lost.`,
            });
            if (!ok) return;
        }
        this.cancelled.emit();
    }

    expandOrCollapseAll() {
        const allCollapsed = this.allCollapsed();
        for (const collapse of this.collapses() ?? []) {
            if (allCollapsed) collapse.expand();
            else collapse.collapse();
        }
    }
}
